import axios from 'axios';

// Isolated client for the Recruiters stack (recruiters.app.lxop.in). Routes are
// served at the domain root (no /v1). Two axios instances share the base URL:
//   • recruiterClient → recruiter session token (lxops_recruiter_token)
//   • memberClient    → the normal member token (lxops_token), for the Hiring page
const BASE_URL =
  (typeof window !== 'undefined' && window.electronEnv?.recruiterApiUrl) ||
  process.env.REACT_APP_RECRUITER_API_URL ||
  'https://recruiters.app.lxop.in';

export const RECRUITER_TOKEN_KEY = 'lxops_recruiter_token';
export const RECRUITER_USER_KEY  = 'lxops_recruiter';

// ── Recruiter-authenticated client ─────────────────────────────────────────────
const recruiterClient = axios.create({ baseURL: BASE_URL, timeout: 15000 });

recruiterClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(RECRUITER_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

recruiterClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(RECRUITER_TOKEN_KEY);
      localStorage.removeItem(RECRUITER_USER_KEY);
      const onAuthPage = typeof window !== 'undefined' && /\/recruiter\/(login|signup)/.test(window.location.href);
      if (typeof window !== 'undefined' && !onAuthPage) {
        // Electron/Capacitor use HashRouter; web uses BrowserRouter.
        const useHash = window.location.protocol === 'file:' || !!window.electronEnv || !!window.Capacitor;
        if (useHash) window.location.hash = '#/recruiter/login';
        else window.location.assign('/recruiter/login');
      }
    }
    return Promise.reject(err);
  },
);

// ── Member-authenticated client (Hiring page apply flow) ───────────────────────
const memberClient = axios.create({ baseURL: BASE_URL, timeout: 15000 });
memberClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('lxops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const data = (p) => p.then((r) => r.data);

// ── Recruiter auth ─────────────────────────────────────────────────────────────
export const recruiterSignup    = (payload) => data(recruiterClient.post('/recruiter/auth/signup', payload));
export const recruiterLogin     = (payload) => data(recruiterClient.post('/recruiter/auth/login', payload));
export const recruiterRequestOtp = (email)  => data(recruiterClient.post('/recruiter/auth/request-otp', { email }));
export const recruiterVerifyOtp = (payload) => data(recruiterClient.post('/recruiter/auth/verify-otp', payload));
export const recruiterGoogle    = (payload) => data(recruiterClient.post('/recruiter/auth/google', payload));
export const recruiterGithub    = (payload) => data(recruiterClient.post('/recruiter/auth/github', payload));
export const getRecruiterMe     = ()        => data(recruiterClient.get('/recruiter/me'));
export const updateRecruiterMe  = (payload) => data(recruiterClient.put('/recruiter/me', payload));
export const changeRecruiterPassword = (payload) => data(recruiterClient.put('/recruiter/me/password', payload));
export const recruiterAvatarUploadUrl = (payload) => data(recruiterClient.post('/recruiter/me/avatar-upload-url', payload));
export const recruiterAvatarConfirm   = (key)     => data(recruiterClient.post('/recruiter/me/avatar-confirm', { key }));

// ── Recruiter jobs ─────────────────────────────────────────────────────────────
export const listMyJobs       = ()              => data(recruiterClient.get('/recruiter/jobs'));
export const createJob        = (payload)       => data(recruiterClient.post('/recruiter/jobs', payload));
export const getJob           = (jobId)         => data(recruiterClient.get(`/recruiter/jobs/${jobId}`));
export const updateJob        = (jobId, payload) => data(recruiterClient.put(`/recruiter/jobs/${jobId}`, payload));
export const deleteJob        = (jobId)         => data(recruiterClient.delete(`/recruiter/jobs/${jobId}`));
export const listJobApplicants = (jobId)        => data(recruiterClient.get(`/recruiter/jobs/${jobId}/applications`));
export const updateApplication = (appId, status) => data(recruiterClient.put(`/recruiter/applications/${appId}`, { status }));

// ── Member-facing (Hiring page) ────────────────────────────────────────────────
export const listPublicJobs   = (params)        => data(memberClient.get('/recruiter/public/jobs', { params }));
export const getPublicJob     = (jobId)         => data(memberClient.get(`/recruiter/public/jobs/${jobId}`));
export const applyToJob       = (jobId, payload) => data(memberClient.post(`/recruiter/public/jobs/${jobId}/apply`, payload));
export const listMyApplications = ()            => data(memberClient.get('/recruiter/public/my-applications'));
export const getRecruiterStats  = ()            => data(memberClient.get('/recruiter/public/stats'));

export default recruiterClient;
